import { type ChangeEvent, type ClipboardEvent, type KeyboardEvent, useEffect, useRef } from 'react';
import { Box, OutlinedInput } from '@mui/material';

interface OtpCodeInputProps {
  value: string;
  onChange: (code: string) => void;
  length?: number;
  autoFocus?: boolean;
  disabled?: boolean;
  onComplete?: (code: string) => void;
}

// Controlled N-digit OTP input. Paste fills all boxes; backspace on empty box jumps back.
export default function OtpCodeInput({
  value,
  onChange,
  length = 6,
  autoFocus,
  disabled,
  onComplete,
}: OtpCodeInputProps) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = value.padEnd(length, ' ').split('').slice(0, length);

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  const setIndex = (i: number, next: string) => {
    const arr = digits.slice();
    arr[i] = next;
    const joined = arr.join('').replace(/\s/g, '');
    onChange(joined);
    if (joined.length === length && onComplete) onComplete(joined);
  };

  const handleChange = (i: number) => (e: ChangeEvent<HTMLInputElement>) => {
    const cleaned = e.target.value.replace(/\D/g, '');
    if (!cleaned) {
      setIndex(i, '');
      return;
    }
    if (cleaned.length === 1) {
      setIndex(i, cleaned);
      if (i < length - 1) refs.current[i + 1]?.focus();
      return;
    }
    const arr = digits.slice();
    for (let k = 0; k < cleaned.length && i + k < length; k += 1) {
      arr[i + k] = cleaned[k];
    }
    const joined = arr.join('').replace(/\s/g, '').slice(0, length);
    onChange(joined);
    const focusAt = Math.min(i + cleaned.length, length - 1);
    refs.current[focusAt]?.focus();
    if (joined.length === length && onComplete) onComplete(joined);
  };

  const handleKey = (i: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[i].trim() && i > 0) {
      e.preventDefault();
      const arr = digits.slice();
      arr[i - 1] = '';
      onChange(arr.join('').replace(/\s/g, ''));
      refs.current[i - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === 'ArrowRight' && i < length - 1) {
      refs.current[i + 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!text) return;
    e.preventDefault();
    onChange(text);
    const focusAt = Math.min(text.length, length - 1);
    refs.current[focusAt]?.focus();
    if (text.length === length && onComplete) onComplete(text);
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
      {Array.from({ length }, (_, i) => (
        <OutlinedInput
          key={i}
          inputRef={(el) => { refs.current[i] = el; }}
          value={digits[i].trim()}
          onChange={handleChange(i)}
          onKeyDown={handleKey(i)}
          onPaste={handlePaste}
          disabled={disabled}
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
            maxLength: 1,
            'aria-label': `digit ${i + 1}`,
            autoComplete: i === 0 ? 'one-time-code' : 'off',
            style: { textAlign: 'center', fontSize: '1.5rem', fontWeight: 600, padding: '14px 0' },
          }}
          sx={{
            width: 52,
            height: 60,
            '& input': { caretColor: 'primary.main' },
          }}
        />
      ))}
    </Box>
  );
}
